import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import DOMPurify from 'dompurify';
import { JeptoUiProgressBarIndeterminateComponent } from '../../../../ui-lib/buttons-indicators/jepto-ui-progress-bar-indeterminate/jepto-ui-progress-bar-indeterminate.component';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { DataService } from '../../../services/data.service';

/**
 * HTML Card Component
 *
 * Renders sanitized HTML content within a card container.
 * Supports standalone mode with direct [html] input or connected mode via [cardId].
 */
@Component({
	selector: 'jepto-html-card',
	templateUrl: './jepto-html-card.component.html',
	styleUrls: ['./jepto-html-card.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatIconModule,
		MatMenuModule,
		JeptoUiProgressBarIndeterminateComponent,
	],
})
export class JeptoHtmlCardComponent extends JeptoBaseCardComponent {
	// Card-specific inputs
	html = input<string>('');
	loading = input<boolean>(false);
	/**
	 * When true (e.g. HTML card editor create preview), show instructional empty
	 * state instead of the generic no-content message.
	 */
	allowEmptyContent = input<boolean>(false);

	// Services
	private dataService = inject(DataService, { optional: true });
	private sanitizer = inject(DomSanitizer);

	// Computed signals
	protected effectiveHtml = computed(() => {
		const direct = this.html();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			return this.dataService.contentDataSignal(id)() ?? '';
		}
		return '';
	});

	/** DOMPurify output marked trusted so Angular does not strip `<style>` (its default [innerHTML] sanitizer removes it). */
	protected sanitizedHtml = computed((): SafeHtml | null => {
		const html = this.effectiveHtml();
		if (!html) return null;

		// Check for liquid code patterns (need server-side processing)
		const liquidPattern = /{%[\s\S]*?%}|{{[\s\S]*?}}/;
		if (liquidPattern.test(html)) return null;

		const pure = DOMPurify.sanitize(html);
		if (!pure) return null;

		return this.sanitizer.bypassSecurityTrustHtml(pure);
	});

	/**
	 * Connected mode (`cardId`): show loading until DataService has emitted content.
	 * Standalone mode (`[html]` only, e.g. editor preview): never use empty HTML as “loading” — render empty state or `innerHTML` instead.
	 */
	protected isLoading = computed(() => {
		if (this.loading()) return true;

		const id = this.cardId();
		if (!id) {
			return false;
		}

		if (this.dataService) {
			const raw = this.dataService.contentDataSignal(id)();
			return raw === undefined;
		}

		return !this.effectiveHtml();
	});

	/** Editor create flow: instructional empty state when HTML is not set yet. */
	protected readonly showCreateHtmlEmptyState = computed(
		() => this.allowEmptyContent() && !this.effectiveHtml()?.trim()
	);
}
